import React, { useEffect, useState } from "react";
import { Checkbox } from "flowbite-react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { getRolePermissions } from "../../../../../../services/workspace/role.service";
import { updateUser } from "../../../../../../services/workspace/user.service";
import { _syncPermissions } from "../../../../../../store/slices/user.slice";
import DefaultButton from "../../../../../components/buttons/defaultButton";

const PermissionsCard = ({ staff, loading, heandleFetchData }) => {
  const [permissionsData, setPermissionsData] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Redux hooks
  const dispatch = useDispatch();
  const { userInfo } = useSelector(state => state.user);
  
  // Check if we're updating the current logged-in user's permissions
  const isCurrentUser = userInfo?.user?.uuid === staff?.uuid;

  // Fetch permissions structure
  useEffect(() => {
    const fetchPermissions = async () => {
      setLoadingPermissions(true);
      try {
        const res = await getRolePermissions();
        // Handle different response structures
        let permissions = [];
        if (res?.permissions) {
          permissions = res.permissions;
        } else if (res?.data?.permissions) {
          permissions = res.data.permissions;
        } else if (Array.isArray(res)) {
          permissions = res;
        } else if (Array.isArray(res?.data)) {
          permissions = res.data;
        }

        if (permissions.length > 0) {
          // Map user permissions to permissionsData structure
          // Check for permissionsArr or permissions field
          const userPermissionsArray = staff?.permissionsArr || staff?.permissions || staff?.user?.permissionsArr || staff?.user?.permissions;
          if (userPermissionsArray && Array.isArray(userPermissionsArray) && userPermissionsArray.length > 0) {
            
            const mappedPermissions = permissions.map((module) => {
              const updatedSubmodules = module.submodules?.map((submodule) => {
                const updatedPermissions = submodule.permissions?.map((permission) => {
                  // Check if this permission name exists in user.permissionsArr array
                  const isSelected = userPermissionsArray.includes(permission.name);
                  return {
                    ...permission,
                    status: isSelected,
                  };
                });
                
                // Update submodule status based on all permissions
                const allPermissionsChecked = updatedPermissions?.every((perm) => perm.status);
                
                return {
                  ...submodule,
                  status: allPermissionsChecked,
                  permissions: updatedPermissions,
                };
              });
              
              // Update module status based on all submodules
              const allSubmodulesChecked = updatedSubmodules?.every((sub) => sub.status);
              
              return {
                ...module,
                status: allSubmodulesChecked,
                submodules: updatedSubmodules,
              };
            });
            
            setPermissionsData(mappedPermissions);
          } else {
            setPermissionsData(permissions);
          }
        }
      } catch (error) {
        console.error("Failed to fetch permissions", error);
      } finally {
        setLoadingPermissions(false);
      }
    };
    
    if (staff) {
      fetchPermissions();
    }
  }, [staff]);

  // Handle module checkbox change
  const handleModuleChange = (moduleIndex, checked) => {
    setPermissionsData((prev) => {
      const updated = [...prev];
      updated[moduleIndex] = {
        ...updated[moduleIndex],
        status: checked,
        submodules: updated[moduleIndex].submodules.map((submodule) => ({
          ...submodule,
          status: checked,
          permissions: submodule.permissions.map((permission) => ({
            ...permission,
            status: checked,
          })),
        })),
      };
      return updated;
    });
  };

  // Handle submodule checkbox change
  const handleSubmoduleChange = (moduleIndex, submoduleIndex, checked) => {
    setPermissionsData((prev) => {
      const updated = [...prev];
      updated[moduleIndex].submodules[submoduleIndex] = {
        ...updated[moduleIndex].submodules[submoduleIndex],
        status: checked,
        permissions: updated[moduleIndex].submodules[submoduleIndex].permissions.map(
          (permission) => ({
            ...permission,
            status: checked,
          })
        ),
      };
      
      // Update module status based on all submodules
      const allSubmodulesChecked = updated[moduleIndex].submodules.every(
        (sub) => sub.status
      );
      updated[moduleIndex].status = allSubmodulesChecked;
      
      return updated;
    });
  };

  // Handle permission checkbox change
  const handlePermissionChange = (moduleIndex, submoduleIndex, permissionIndex, checked) => {
    setPermissionsData((prev) => {
      const updated = [...prev];
      updated[moduleIndex].submodules[submoduleIndex].permissions[permissionIndex] = {
        ...updated[moduleIndex].submodules[submoduleIndex].permissions[permissionIndex],
        status: checked,
      };
      
      // Update submodule status based on all permissions
      const allPermissionsChecked = updated[moduleIndex].submodules[submoduleIndex].permissions.every(
        (perm) => perm.status
      );
      updated[moduleIndex].submodules[submoduleIndex].status = allPermissionsChecked;
      
      // Update module status based on all submodules
      const allSubmodulesChecked = updated[moduleIndex].submodules.every(
        (sub) => sub.status
      );
      updated[moduleIndex].status = allSubmodulesChecked;
      
      return updated;
    });
  };

  // Handle save permissions
  const handleSavePermissions = async () => {
    if (  !staff?.uuid) {
      toast.error("User ID not found!");
      return;
    }

    setIsSaving(true);
    try {
      // Extract only permissions with status: true (same format as role)
      const selectedPermissions = [];
      permissionsData.forEach((module) => {
        module.submodules?.forEach((submodule) => {
          submodule.permissions?.forEach((permission) => {
            if (permission.status === true) {
              selectedPermissions.push(permission.name);
            }
          });
        });
      });

      const userId =  staff?.uuid;
      const payload = {
        permissions: selectedPermissions,
      };

      await updateUser(payload, userId);
      toast.success("Permissions updated successfully!");
      setIsEditMode(false);
      
      // Sync permissions with Redux store if updating current user and user is not type 1
      if (isCurrentUser && userInfo?.user?.type !== 1) {
        // Create the permission object in the format expected by the transformation function
        const permissionObject = {};
        selectedPermissions.forEach(perm => {
          const [module, submodule] = perm.split('-');
          if (module && submodule) {
            if (!permissionObject[module]) {
              permissionObject[module] = {};
            }
            if (!permissionObject[module][submodule]) {
              permissionObject[module][submodule] = [];
            }
            permissionObject[module][submodule].push(perm);
          }
        });
        
        dispatch(_syncPermissions(permissionObject));
      }
      
      // Refresh staff data
      if (heandleFetchData) {
        heandleFetchData();
      }
    } catch (error) {
      console.error("Failed to update permissions", error);
      toast.error(error?.response?.data?.message || "Failed to update permissions!");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reload permissions to reset changes
    if (staff) {
      const fetchPermissions = async () => {
        setLoadingPermissions(true);
        try {
          const res = await getRolePermissions();
          let permissions = [];
          if (res?.permissions) {
            permissions = res.permissions;
          } else if (res?.data?.permissions) {
            permissions = res.data.permissions;
          } else if (Array.isArray(res)) {
            permissions = res;
          } else if (Array.isArray(res?.data)) {
            permissions = res.data;
          }

          if (permissions.length > 0) {
            const userPermissionsArray = staff?.permissionsArr || staff?.permissions || staff?.user?.permissionsArr || staff?.user?.permissions;
            if (userPermissionsArray && Array.isArray(userPermissionsArray) && userPermissionsArray.length > 0) {
              const mappedPermissions = permissions.map((module) => {
                const updatedSubmodules = module.submodules?.map((submodule) => {
                  const updatedPermissions = submodule.permissions?.map((permission) => {
                    const isSelected = userPermissionsArray.includes(permission.name);
                    return {
                      ...permission,
                      status: isSelected,
                    };
                  });
                  const allPermissionsChecked = updatedPermissions?.every((perm) => perm.status);
                  return {
                    ...submodule,
                    status: allPermissionsChecked,
                    permissions: updatedPermissions,
                  };
                });
                const allSubmodulesChecked = updatedSubmodules?.every((sub) => sub.status);
                return {
                  ...module,
                  status: allSubmodulesChecked,
                  submodules: updatedSubmodules,
                };
              });
              setPermissionsData(mappedPermissions);
            } else {
              setPermissionsData(permissions);
            }
          }
        } catch (error) {
          console.error("Failed to fetch permissions", error);
        } finally {
          setLoadingPermissions(false);
        }
      };
      fetchPermissions();
    }
  };

  if (loading || loadingPermissions) {
    return (
      <div className="bg-white p-4 rounded-md">
        <div className="text-center py-4 text-gray-500">Loading permissions...</div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-md">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">User Permissions</h3>
          <p className="text-sm text-gray-600 mt-1">
            {isEditMode 
              ? "Select the permissions you want to assign to this user." 
              : "View the permissions assigned to this user."}
          </p>
        </div>
        {!isEditMode && (
          <DefaultButton
            variant="default"
            name="Edit"
            onClick={() => setIsEditMode(true)}
          />
        )}
      </div>

      {isEditMode && (
        <div className="flex justify-end gap-3 mb-4 pb-4 border-b">
          <DefaultButton
            variant="outline"
            name="Cancel"
            onClick={handleCancelEdit}
            disabled={isSaving}
          />
          <DefaultButton
            variant="default"
            name="Save"
            onClick={handleSavePermissions}
            loading={isSaving}
            disabled={isSaving}
          />
        </div>
      )}

      {permissionsData.length === 0 ? (
        <div className="text-center py-4 text-gray-500">No permissions available</div>
      ) : (
        <div className="flex flex-col gap-4">
          {permissionsData.map((module, moduleIndex) => {
            const moduleName = module.module?.replace(/-/g, " ") || "Module";
            const hasSelectedPermissions = module.submodules?.some(sub => 
              sub.permissions?.some(perm => perm.status)
            );
            
            return (
              <div
                key={moduleIndex}
                className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Module Header */}
                <div className={`flex items-center gap-3 px-4 py-3 ${
                  hasSelectedPermissions ? 'bg-gradient-to-r from-blue-50 to-indigo-50' : 'bg-gray-50'
                } border-b border-gray-200`}>
                  {isEditMode && (
                    <Checkbox
                      id={`module-${moduleIndex}`}
                      checked={!!module.status}
                      onChange={(e) => handleModuleChange(moduleIndex, e.target.checked)}
                      className="w-5 h-5"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold text-base capitalize text-gray-800">
                      {moduleName}
                    </h4>
                    {!isEditMode && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {module.submodules?.reduce((acc, sub) => 
                          acc + (sub.permissions?.filter(p => p.status).length || 0), 0
                        )} permission(s) assigned
                      </p>
                    )}
                  </div>
                  {!isEditMode && hasSelectedPermissions && (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                      Active
                    </span>
                  )}
                </div>

                {/* Submodules */}
                <div className="p-4 space-y-4">
                  {module.submodules?.map((submodule, submoduleIndex) => {
                    const submoduleName = submodule.name?.replace(/-/g, " ") || "Submodule";
                    const selectedPermissions = submodule.permissions?.filter(p => p.status) || [];
                    const hasSelected = selectedPermissions.length > 0;
                    
                    return (
                      <div
                        key={submoduleIndex}
                        className={`rounded-lg border ${
                          hasSelected 
                            ? 'border-blue-200 bg-blue-50/30' 
                            : 'border-gray-100 bg-gray-50/50'
                        } p-4 transition-all`}
                      >
                        {/* Submodule Header */}
                        <div className="flex items-center gap-3 mb-3">
                          {isEditMode && (
                            <Checkbox
                              id={`submodule-${moduleIndex}-${submoduleIndex}`}
                              checked={!!submodule.status}
                              onChange={(e) =>
                                handleSubmoduleChange(
                                  moduleIndex,
                                  submoduleIndex,
                                  e.target.checked
                                )
                              }
                              className="w-4 h-4"
                            />
                          )}
                          <h5 className={`font-medium text-sm capitalize flex-1 ${
                            hasSelected ? 'text-gray-800' : 'text-gray-600'
                          }`}>
                            {submoduleName}
                          </h5>
                          {!isEditMode && (
                            <span className="text-xs text-gray-500">
                              {selectedPermissions.length} / {submodule.permissions?.length || 0}
                            </span>
                          )}
                        </div>

                        {/* Permissions */}
                        {isEditMode ? (
                          <div className="ml-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {submodule.permissions?.map((permission, permissionIndex) => (
                              <label
                                key={permissionIndex}
                                className="flex items-center gap-2 p-2 rounded-md hover:bg-white cursor-pointer transition-colors"
                              >
                                <Checkbox
                                  id={`permission-${moduleIndex}-${submoduleIndex}-${permissionIndex}`}
                                  checked={!!permission.status}
                                  onChange={(e) =>
                                    handlePermissionChange(
                                      moduleIndex,
                                      submoduleIndex,
                                      permissionIndex,
                                      e.target.checked
                                    )
                                  }
                                  className="w-4 h-4"
                                />
                                <span className="text-sm capitalize text-gray-700">
                                  {permission.name
                                    ?.replace(/-/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase()) || "Permission"}
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="ml-7 flex flex-wrap gap-2">
                            {selectedPermissions.length > 0 ? (
                              selectedPermissions.map((permission, permissionIndex) => {
                                const permissionName = permission.name
                                  ?.replace(/-/g, " ")
                                  .replace(/\b\w/g, (l) => l.toUpperCase()) || "Permission";
                                const permissionType = permission.name?.split('-')[1] || 'view';
                                
                                const badgeColors = {
                                  view: 'bg-blue-100 text-blue-700 border-blue-200',
                                  create: 'bg-green-100 text-green-700 border-green-200',
                                  update: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                                  delete: 'bg-red-100 text-red-700 border-red-200',
                                };
                                
                                const badgeColor = badgeColors[permissionType] || 'bg-gray-100 text-gray-700 border-gray-200';
                                
                                return (
                                  <span
                                    key={permissionIndex}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-full border ${badgeColor} flex items-center gap-1.5`}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60"></span>
                                    {permissionName}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-xs text-gray-400 italic">No permissions assigned</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PermissionsCard;
